import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom-v5-compat';
import { Badge } from 'src/components/atoms';
import { OnRowClickType } from 'src/components/molecules';
import {
  ReportStatus,
  ResponseStatus,
  useGetOrganisationFormResponsesPaginatedQuery,
} from 'src/graphql';
import { useTableColumns } from 'src/hooks';
import { getReadableIdWithDash } from 'src/types';
import { DateFormats, formatDate } from 'src/utils/time';

const columns = [
  { label: 'Case ID', value: 'caseId' },
  { label: 'Form Title', value: 'formTitle' },
  { label: 'Category', value: 'category' },
  { label: 'Employee', value: 'employee' },
  { label: 'Submitted', value: 'submitted' },
  { label: 'Status', value: 'status' },
  { label: 'Flags', value: 'flags' },
  { label: 'Reviewer', value: 'reviewer' },
];

const sortableColumns = [
  'Form Title',
  'Category',
  'Employee',
  'Submitted',
  'Status',
  'Flags',
  'Reviewer',
];

const RESPONSE_STATUS_LABELS: Record<ResponseStatus, string> = {
  [ResponseStatus.Approved]: 'Approved',
  [ResponseStatus.Draft]: 'Draft',
  [ResponseStatus.Flagged]: 'Flagged',
  [ResponseStatus.Submitted]: 'Submitted',
  [ResponseStatus.UnderReview]: 'Under review',
  [ResponseStatus.AutoClosed]: 'Auto closed',
};

const normalizeStatus = (status: string): ResponseStatus | undefined => {
  const normalized = status.toUpperCase();
  return Object.values(ResponseStatus).find((s) => s === normalized);
};

const COI_RESPONSE_STATUS_OPTIONS = (
  Object.values(ResponseStatus) as ResponseStatus[]
).map((value) => ({
  value,
  label: RESPONSE_STATUS_LABELS[value],
}));

export interface CoiFilters {
  statuses?: string[];
  reviewer?: string[];
  submittedDate?: {
    from: string;
    to: string;
  };
  formTitle?: string[];
  category?: string[];
  textFilterLogic?: 'and' | 'or';
}

export const useOrganisationCoiResponsesTable = (
  rows: number,
  page: number,
  filters?: CoiFilters,
  searchTerm?: string,
) => {
  const navigate = useNavigate();
  const { visibleColumns, sortedColumn, sortColumns, updateVisibleColumns } =
    useTableColumns(rows, page, columns, [], 'submitted', 'desc', [], {});

  const queryParams = useMemo(() => {
    const params: {
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: string;
      status?: ResponseStatus;
      submittedFrom?: string;
      submittedTo?: string;
      formCategoryKey?: string;
    } = {
      page,
      limit: rows,
      formCategoryKey: 'disclosure',
    };

    if (sortedColumn.columnName) {
      const columnValue = columns.find(
        (c) => c.label === sortedColumn.columnName,
      )?.value;
      if (columnValue) {
        const sortByMap: Record<string, string> = {
          formTitle: 'questionnaire.publicTitle',
          employee: 'respondent.name',
          submitted: 'submitted_at',
          status: 'status',
        };
        params.sortBy = sortByMap[columnValue] || columnValue;
        params.sortOrder = sortedColumn.direction === 'asc' ? 'asc' : 'desc';
      }
    }

    if (filters?.statuses?.length) {
      params.status = filters.statuses[0].toUpperCase() as ResponseStatus;
    }

    if (filters?.submittedDate) {
      const submittedFrom = formatDate(
        filters.submittedDate.from,
        DateFormats.YYYY_MM_DD,
        true,
        undefined,
      );
      const submittedTo = formatDate(
        filters.submittedDate.to,
        DateFormats.YYYY_MM_DD,
        true,
        undefined,
      );
      if (submittedFrom && submittedTo) {
        if (submittedFrom > submittedTo) {
          params.submittedFrom = submittedTo;
          params.submittedTo = submittedFrom;
        } else {
          params.submittedFrom = submittedFrom;
          params.submittedTo = submittedTo;
        }
      }
    }

    return params;
  }, [page, rows, sortedColumn, filters]);

  const {
    data,
    isLoading,
    isFetching,
    isError,
    refetch: refetchFormResponses,
  } = useGetOrganisationFormResponsesPaginatedQuery(
    {
      queryParams:
        Object.keys(queryParams).length > 0 ? queryParams : undefined,
    },
    {
      enabled: true,
    },
  );

  const responses = useMemo(
    () => data?.organisationFormResponsesPaginated?.responses ?? [],
    [data?.organisationFormResponsesPaginated?.responses],
  );

  const filteredFormResponses = useMemo(() => {
    let result = responses;

    // Simple text search across all fields
    if (searchTerm && searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      result = result.filter((response) => {
        const submissionId = response.id?.toLowerCase() || '';
        const formattedId =
          getReadableIdWithDash(response.id)?.toLowerCase() || '';
        const employeeName = response.respondent?.name?.toLowerCase() || '';
        const employeeId = response.respondent?.userId?.toLowerCase() || '';
        const formTitle =
          response.questionnaire?.publicTitle?.toLowerCase() || '';
        const normalizedStatus =
          response.status && normalizeStatus(response.status);
        const status = normalizedStatus
          ? RESPONSE_STATUS_LABELS[normalizedStatus]?.toLowerCase()
          : '';

        return (
          submissionId.includes(term) ||
          formattedId.includes(term) ||
          employeeName.includes(term) ||
          employeeId.includes(term) ||
          formTitle.includes(term) ||
          status.includes(term)
        );
      });
    }

    const reviewerTerm = filters?.reviewer?.[0]?.trim().toLowerCase();
    const categoryTerm = filters?.category?.[0]?.trim().toLowerCase();
    const formTitleTerm = filters?.formTitle?.[0]?.trim().toLowerCase();

    const predicates: Array<(r: (typeof result)[0]) => boolean> = [];
    if (reviewerTerm) {
      predicates.push((r) =>
        Boolean(
          r.flagInfo?.flaggedBy?.name?.toLowerCase().includes(reviewerTerm),
        ),
      );
    }
    if (categoryTerm) {
      predicates.push((r) =>
        Boolean(
          r.flagInfo?.category?.toString().toLowerCase().includes(categoryTerm),
        ),
      );
    }
    if (formTitleTerm) {
      predicates.push((r) =>
        Boolean(
          r.questionnaire?.publicTitle?.toLowerCase().includes(formTitleTerm),
        ),
      );
    }

    if (predicates.length) {
      result =
        filters?.textFilterLogic === 'or'
          ? result.filter((r) => predicates.some((p) => p(r)))
          : result.filter((r) => predicates.every((p) => p(r)));
    }

    return result;
  }, [
    responses,
    searchTerm,
    filters?.reviewer,
    filters?.category,
    filters?.formTitle,
    filters?.textFilterLogic,
  ]);

  const reviewerOptions = useMemo(() => {
    const names = new Set<string>();
    for (const r of responses) {
      const name = r.flagInfo?.flaggedBy?.name?.trim();
      if (name) names.add(name);
    }
    return Array.from(names).map((value) => ({ value, label: value }));
  }, [responses]);

  const formTitleOptions = useMemo(() => {
    const titles = new Set<string>();
    for (const r of responses) {
      const title = r.questionnaire?.publicTitle;
      if (title) titles.add(title);
    }
    return Array.from(titles).map((value) => ({ value, label: value }));
  }, [responses]);

  const categoryOptions = useMemo(() => {
    const categories = new Set<string>();
    for (const r of responses) {
      const category = r?.flagInfo?.category;
      if (category) categories.add(category);
    }
    return Array.from(categories).map((value) => ({ value, label: value }));
  }, [responses]);

  const tableData = filteredFormResponses.map((response) => {
    const reviewerName = response.flagInfo?.flaggedBy?.name?.trim() ?? '-';
    return {
      cellData: [
        getReadableIdWithDash(response.id?.toLocaleUpperCase()),
        response.questionnaire?.publicTitle ?? '-',
        '',
        response.respondent?.name ?? '-',
        formatDate(
          response.submittedAt ?? null,
          DateFormats.DAY_MONTH_YEAR,
          true,
          '-',
        ),
        <Badge
          key={response.id ?? ''}
          variant="new"
          text={
            (response.status &&
              RESPONSE_STATUS_LABELS[normalizeStatus(response.status)!]) ??
            '-'
          }
        />,
        '0',
        reviewerName,
      ],
      row: response.id ?? '',
      data: {
        id: response.id ?? '',
        status: response.status,
      },
    };
  });

  const pagination = data?.organisationFormResponsesPaginated?.pagination;
  const disclosuresCount = pagination?.totalItems ?? 0;

  const statusOptions = COI_RESPONSE_STATUS_OPTIONS;

  const openDisclosure: OnRowClickType<{
    status: ReportStatus;
    reportId: string;
    existingReport: boolean;
  }> = (caseId) => {
    navigate(`auto-closed-disclosure?id=${caseId}`);
  };

  return {
    columns,
    sortableColumns,
    visibleColumns,
    columnInfoTooltips: [],
    updateVisibleColumns,
    tableData,
    isLoading,
    isFetching,
    isError,
    sortColumns,
    sortedColumn,
    count: disclosuresCount,
    totalPages: pagination?.totalPages ?? 1,
    refetchFormResponses,
    statusOptions,
    reviewerOptions,
    openDisclosure,
    formTitleOptions,
    categoryOptions,
  };
};
